import { AgBytesAPI } from "@ai360/core";

import { getLastUsedLocationGuid, getTheUserCompanyGuid } from "~/login/selectors";
import { useAppSelector } from "~/store/hooks";

export const IS_SELECTABLE = "isSelectable";

/**
 * Not folded into `activeYn` -- Org Level Products needs inactive products still visible. Hiding is
 * cosmetic either way (`optionIsHiddenKey` doesn't block keyboard/typeahead selection), so surfaces
 * still need their own marking and Save-block.
 */
export const computeIsSelectable = (
    activeYn: boolean,
    isAgvanceLinked: boolean | null,
    showInactive: boolean = false
): boolean => (showInactive || activeYn) && isAgvanceLinked !== false;

const EMPTY_PRODUCTS: AgBytesAPI.IProductSelectionListItem[] = [];
const EMPTY_FILTERS: AgBytesAPI.IProductSelectionListFilters = {};

export interface IUseProductListOptions {
    customerGuid?: string | null;
    filters?: AgBytesAPI.IProductSelectionListFilters;
    isEnabled?: boolean;
    orgLevelGuid?: string | null;
    scope: AgBytesAPI.ProductSelectionListScope;
}

export interface IUseProductListResult {
    isFetching: boolean;
    products: AgBytesAPI.IProductSelectionListItem[];
}

/**
 * One RTK cache entry over `POST agbytes/v1.0/product`. Use this instead of a per-surface
 * fetch, so identical arguments share one request.
 */
export const useProductList = ({
    customerGuid = null,
    filters = EMPTY_FILTERS,
    isEnabled = true,
    orgLevelGuid = null,
    scope,
}: IUseProductListOptions): IUseProductListResult => {
    const isScopeResolvable = Boolean(customerGuid || orgLevelGuid);
    const { data: products = EMPTY_PRODUCTS, isFetching } =
        AgBytesAPI.useGetProductSelectionListQuery(
            {
                scope,
                filters,
                ...(customerGuid && { customerGuid }),
                ...(!customerGuid && orgLevelGuid != null && { orgLevelGuid }),
            },
            { skip: !isEnabled || !isScopeResolvable, refetchOnMountOrArgChange: true }
        );

    return { isFetching, products };
};

/**
 * The logged-in user's own org level, for `UserHierarchy`-scoped product lists. Every such surface
 * should resolve it through this one selector -- two surfaces computing it differently would each get
 * their own cache entry instead of sharing one.
 */
export const getLoggedInUserProductScopeOrgLevelGuid = (state: unknown): string | null =>
    getLastUsedLocationGuid(state) || getTheUserCompanyGuid(state) || null;

export const useLoggedInUserProductScopeOrgLevelGuid = (): string | null =>
    useAppSelector(getLoggedInUserProductScopeOrgLevelGuid);

export interface IProductDropdownOption {
    activeYn: boolean;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useProductList()` result to the plain option shape `RestrictedProductList`/`ProductList`
 * expect.
 */
export const toProductDropdownOptions = (
    products: AgBytesAPI.IProductSelectionListItem[]
): IProductDropdownOption[] =>
    products.map((product) => ({
        activeYn: product.activeYn,
        guid: product.productGuid,
        id: String(product.productId),
        isAgvanceLinked: product.isAgvanceLinked,
        name: product.name,
    }));
